import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import turfDistance from '@turf/distance';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { pointItemToLngLat } from 'utils/mapbox/sources/point_items';
import { MapStatsUtil } from 'utils/map_stats_util';
import { SearchResultsUtil } from 'utils/search_results_util';
import { logTrailCardClicked, TrailDetailsReferralSources } from 'utils/trail_analytics_helpers';
import HighlightedResultName from '../HighlightedResultName/HighlightedResultName';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import SearchResultIcon from '../SearchResultIcon';
import * as styles from './styles/styles.module.scss';

const Info = ({ className, title }) => {
  return (
    <span className={classNames(className, StringConstantsMixin.constants.XLATE.NONE)} title={title}>
      {title}
    </span>
  );
};

Info.propTypes = {
  className: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};

const SearchResultItem = ({
  term,
  data,
  idx,
  selected,
  displayMetric,
  languageRegionCode,
  latLngForDisplayDistance,
  hoverHandle,
  handleItemClick
}) => {
  const { highlightedName, url } = SearchResultsUtil.formatSearchResult(data);

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    _gaq.push(['_trackEvent', 'Site Search', term, data.slug, idx + 1]);
    if (data.type === 'trail') {
      logTrailCardClicked(data.ID, TrailDetailsReferralSources.Search);
    }

    handleItemClick(url, data, idx);
  };

  let formattedName = LanguageSupportUtil.findRecordFieldWithLocale(data, 'name', languageRegionCode);
  if (latLngForDisplayDistance) {
    const lngLat = latLngForDisplayDistance.slice().reverse();
    const areaDistanceTo = turfDistance(pointItemToLngLat(data), lngLat) * 1000;
    const formattedDistance = MapStatsUtil.metersToFormattedUserUnits(areaDistanceTo, 2, displayMetric, languageRegionCode);
    formattedName = `${formattedName}  (${formattedDistance})`;
  }

  return (
    <div
      role="presentation"
      className={classNames('algolia-hit', { 'hit-selected': selected, [styles.selected]: selected })}
      onMouseEnter={() => hoverHandle(idx)}
      onTouchStart={() => hoverHandle(idx)}
      onClick={handleClick}
    >
      <a className={styles.hitLink} href={LanguageSupportUtil.wrapUrlSafe(url, languageRegionCode)}>
        <SearchResultIcon isActive={selected} isSmallVariant result={data} />
        <span className="search-text">
          <span className={classNames(styles.name, StringConstantsMixin.constants.XLATE.NONE)} title={formattedName}>
            <HighlightedResultName highlightedName={highlightedName} />
          </span>
          <br />
          <Info className="trail-search-info" title={data.type === 'trail' ? SearchResultsUtil.formatTrailInfo(data, languageRegionCode) : ''} />
          <Info
            className="location-search-info"
            title={data.type !== 'trail' ? SearchResultsUtil.formatNonTrailInfo(data, languageRegionCode) : ''}
          />
        </span>
      </a>
    </div>
  );
};

SearchResultItem.propTypes = {
  term: PropTypes.string,
  data: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired
  }).isRequired,
  idx: PropTypes.number.isRequired,
  selected: PropTypes.bool.isRequired,
  displayMetric: PropTypes.bool,
  languageRegionCode: PropTypes.string.isRequired,
  latLngForDisplayDistance: PropTypes.func,
  hoverHandle: PropTypes.func.isRequired,
  handleItemClick: PropTypes.func.isRequired
};

SearchResultItem.defaultProps = {
  term: '',
  displayMetric: false,
  latLngForDisplayDistance: null
};

export default SearchResultItem;
